import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ScaleIcon from '@mui/icons-material/ScaleOutlined';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeightOutlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import { apiClient } from '@/api/axiosClient';
import StatCard from '@/components/common/StatCard';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupCountBy, groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import { withId } from '@/pages/reports/reportHelpers';
import type { WeightProductionPlanRegisterRow, WeightProductionPendingJobCompletionRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

/** Weigh & Label dashboard tab. Rendered only while ProcessConfiguration.WeightProductionEnabled is
 * on — the tab itself is gated in Dashboard.tsx's CATEGORY_TABS. */
export default function WeightProductionPanel() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: plans = [], isLoading: loadingPlans } = useQuery({
    queryKey: ['reports', 'weight-production-plan-register'],
    queryFn: async () => (await apiClient.get<WeightProductionPlanRegisterRow[]>('/reports/weight-production-plan-register')).data,
  });
  const { data: pending = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reports', 'weight-production-pending-job-completion'],
    queryFn: async () => (await apiClient.get<WeightProductionPendingJobCompletionRow[]>('/reports/weight-production-pending-job-completion')).data,
  });

  const totalPlans = useMemo(() => new Set(plans.map((r) => r.orderId)).size, [plans]);
  const plannedQty = useMemo(() => plans.reduce((sum, r) => sum + r.plannedQty, 0), [plans]);
  const producedQty = useMemo(() => plans.reduce((sum, r) => sum + r.producedQty, 0), [plans]);
  const overdueCount = useMemo(() => pending.filter((r) => r.ageInDays >= 1).length, [pending]);

  const plannedVsProduced = useMemo(
    () => groupSumByMonth(plans, (r) => r.plannedStartDate, [
      { key: 'planned', value: (r) => r.plannedQty },
      { key: 'produced', value: (r) => r.producedQty },
    ]),
    [plans],
  );
  const statusDistribution = useMemo(() => groupCountBy(plans, (r) => r.status), [plans]);
  // What's actually sitting unconfirmed right now — the single thing this tab is watched for.
  const topPending = useMemo(() => topN(pending, (r) => r.ageInDays, 10).reverse(), [pending]);

  const pendingColumns: DataTableColumn<ReturnType<typeof withId<WeightProductionPendingJobCompletionRow>>[number]>[] = [
    { field: 'barcodeNo', headerName: tr('columns.barcode') },
    { field: 'materialCode', headerName: tr('columns.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'qty', headerName: tr('columns.qty'), align: 'right' },
    { field: 'actualWeight', headerName: tr('columns.actualWeight'), align: 'right', render: (r) => `${r.actualWeight} ${r.weightUom}` },
    {
      field: 'ageInDays', headerName: tr('columns.age'), align: 'right',
      render: (r) => (r.ageInDays >= 1
        ? <Chip size="small" color="warning" label={tr('columns.days', { days: r.ageInDays })} />
        : tr('columns.days', { days: r.ageInDays })),
    },
  ];

  return (
    <Stack spacing={3}>
      <GridContainer columns={12}>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('weightProduction.totalPlans')} value={totalPlans} icon={<ScaleIcon />} accentColor={colors.weightProduction} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('weightProduction.plannedQty')} value={plannedQty.toLocaleString()} icon={<MonitorWeightIcon />} accentColor={colors.weightProduction} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard label={t('weightProduction.producedQty')} value={producedQty.toLocaleString()} icon={<MonitorWeightIcon />} accentColor={colors.production} />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <StatCard
            label={overdueCount > 0 ? t('weightProduction.overdueEntries') : t('weightProduction.pendingJobCompletion')}
            value={overdueCount > 0 ? overdueCount : pending.length}
            icon={overdueCount > 0 ? <WarningAmberIcon /> : <PendingActionsIcon />}
            accentColor={colors.adjustments}
          />
        </GridItem>
      </GridContainer>

      <GridContainer columns={12} spacing={2}>
        <GridItem xs={12} md={7}>
          <ChartCard title={t('weightProduction.plannedVsProducedTitle')} subtitle={t('weightProduction.plannedVsProducedSubtitle')} loading={loadingPlans} empty={!loadingPlans && plannedVsProduced.length === 0}>
            <DashboardBarChart
              categories={plannedVsProduced.map((m) => m.month)}
              series={[
                { label: tr('columns.plannedQty'), data: plannedVsProduced.map((m) => m.values.planned), color: colors.procurement },
                { label: tr('columns.producedQty'), data: plannedVsProduced.map((m) => m.values.produced), color: colors.weightProduction },
              ]}
            />
          </ChartCard>
        </GridItem>
        <GridItem xs={12} md={5}>
          <ChartCard title={t('weightProduction.statusTitle')} loading={loadingPlans} empty={!loadingPlans && statusDistribution.length === 0}>
            <DashboardPieChart data={statusDistribution.map((s, i) => ({ label: s.key, value: s.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))} />
          </ChartCard>
        </GridItem>
      </GridContainer>

      <ChartCard title={t('weightProduction.pendingByAgeTitle')} loading={loadingPending} empty={!loadingPending && topPending.length === 0} height={300}>
        <DashboardBarChart
          layout="horizontal"
          categories={topPending.map((r) => r.barcodeNo)}
          series={[{ label: tr('columns.age'), data: topPending.map((r) => r.ageInDays), color: colors.weightProduction }]}
          height={300}
        />
      </ChartCard>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {t('weightProduction.pendingTableTitle')}
        </Typography>
        <DataTable columns={pendingColumns} rows={withId(pending, (r) => r.entryId)} loading={loadingPending} exportable={false} defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
    </Stack>
  );
}
